import { infiniteQueryOptions, queryOptions, type QueryClient } from '@tanstack/react-query'
import {
  getConversationOptions,
  getConversationTimelineOptions,
  listConversationsInfiniteOptions,
  listTriageInfiniteOptions,
  nextCursorParam,
  POLLING,
  STALE,
  type ConversationList,
  type ListConversationsData,
  type TriageList,
} from '@/shared/api'

type ConversationsQueryParams = NonNullable<ListConversationsData['query']>

// Выведен из контракта, а не переписан литералом: рукописная копия молча не заметила бы новый
// вариант фильтра, добавленный на бекенде.
export type ConversationsFilter = NonNullable<ConversationsQueryParams['filter']>

/**
 * Список диалогов — операционный экран «Чаты». Курсорный: сервер отдаёт его страницами (7.1), и не
 * листать их значило бы показывать первую страницу как весь ящик.
 *
 * ⚠️ ПОЛЛИНГ ЖИВЁТ ТОЛЬКО НА ПЕРВОЙ СТРАНИЦЕ. `refetchInterval` у infinite-запроса перезапрашивает
 * КАЖДУЮ загруженную страницу, а не последнюю. Но и убрать его нельзя: инбокс — операционная
 * очередь, новые сообщения обязаны появляться сами. Отсюда развилка по числу страниц: пока страница
 * одна (а разбирают ВЕРХ списка), поллинг обычный; ушли вглубь — молчит.
 *
 * Вместо него на глубине: `refetchOnWindowFocus` — ⚠️ он ТОЖЕ перечитывает все страницы, но один раз
 * на возврат в окно, а не по таймеру, — плюс инвалидация от собственных действий и счётчик вкладки
 * со своей ручкой `getBadges`.
 *
 * ⚠️ Честная цена: обратного пути к одной странице нет — нажав «Показать ещё», человек гасит поллинг
 * до конца жизни записи в кеше. Подрезать страницы автоматически было бы хуже: содержимое исчезало
 * бы у него под курсором.
 */
export function conversationsQuery(filter: ConversationsFilter) {
  return infiniteQueryOptions({
    ...listConversationsInfiniteOptions({ query: { filter } }),
    // `{}`, а не `''`: кодоген подмешивает объект в query как есть, а пустая строка уехала бы как
    // `cursor=` — это не то же самое, что «курсора нет».
    initialPageParam: {},
    getNextPageParam: (lastPage: ConversationList) => nextCursorParam(lastPage.nextCursor),
    staleTime: STALE.live,
    refetchInterval: (query) =>
      (query.state.data?.pages.length ?? 1) > 1 ? false : POLLING.inbox,
  })
}

/**
 * Шапка треда — гость, каналы, режим, черновик ИИ, статус, привязанная бронь.
 *
 * Опрашивается, как и лента: когда гость отвечает, сервер выкладывает `aiDraft` и меняет
 * `statusNote`, а это меняется НЕ нами — без поллинга появлялось бы только на фокусе окна. Свои
 * действия по-прежнему разносят мутации.
 */
export function conversationQuery(conversationId: string) {
  return queryOptions({
    ...getConversationOptions({ path: { conversationId } }),
    staleTime: STALE.live,
    refetchInterval: POLLING.conversation,
  })
}

/**
 * Очередь разбора — входящие, не привязанные ни к одной брони.
 *
 * Постраничный ИМЕННО потому, что страница бывает пустой при живом курсоре (см. `model/triage.ts`):
 * дочитывать обязан сам экран, и без страниц ему нечем. Поллинга нет намеренно — очередь
 * наполняется редко, а перезапрос уносил бы строку, которую человек как раз читает.
 */
export function triageQuery() {
  return infiniteQueryOptions({
    ...listTriageInfiniteOptions(),
    initialPageParam: {},
    getNextPageParam: (lastPage: TriageList) => nextCursorParam(lastPage.nextCursor),
    staleTime: STALE.live,
  })
}

/**
 * Лента переписки — ОБЫЧНЫЙ `queryOptions`, а НЕ `infiniteQueryOptions`, хотя кодоген рядом
 * генерирует и infinite-вариант (курсор `before`/`nextCursor` в контракте есть).
 *
 * ⚠️ ПОЛЛИНГ И КУРСОРНАЯ ПАГИНАЦИЯ НА ОДНОМ ЗАПРОСЕ НЕСОВМЕСТИМЫ — доказано зондом: `refetchInterval`
 * дёргает КАЖДУЮ загруженную страницу, чтобы сохранить их согласованность. При тике раз в 5 секунд и
 * четырёх подгруженных страницах это четыре запроса на тик — ровно та нагрузка, ради избавления от
 * которой контракт построен по принципу «чтение — под экран». Один раз это уже случилось с
 * `POLLING.bookings`: константу завели и убрали.
 *
 * ⚠️ ХОТИТЕ «ПОЧИНИТЬ» НА INFINITE — НЕ НАДО: пагинация истории это отдельная задача с отдельным,
 * не поллинговым источником свежести. Её отсутствие не упущение: ни в карточке брони, ни на экране
 * «Чаты» кнопки «показать раньше» нет вовсе (`docs/reference/phase-2-mockup.md`, разделы 3 и 4),
 * дизайн исходит из того, что переписка по одной брони помещается целиком.
 *
 * Каждый тик перезапрашивает последний снимок ЦЕЛИКОМ — это дёшево: история одной брони, не всей
 * компании.
 */
export function timelineQuery(conversationId: string) {
  return queryOptions({
    ...getConversationTimelineOptions({ path: { conversationId } }),
    staleTime: STALE.live,
    refetchInterval: POLLING.conversation,
  })
}

/** Прогрев треда по наведению: клик открывает диалог из тёплого кеша, а не с двойным скелетоном. */
export function prefetchConversationThread(queryClient: QueryClient, conversationId: string) {
  return Promise.all([
    queryClient.prefetchQuery(conversationQuery(conversationId)),
    queryClient.prefetchQuery(timelineQuery(conversationId)),
  ])
}
